import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react'
import { Icon } from '@bloo-team/ui'
import { Play, Plus, X } from '@bloo-team/ui/icons'
import { PickList } from '@bloo-team/ui'
import { TimeRangePicker } from '../TimeRangePicker'
import {
  AGG_FUNCTIONS,
  bareField,
  buildDql,
  DQL_OPERATORS,
  type DqlAggregation,
  type DqlFilter,
  type DqlTime,
  type SearchWindow,
} from '../../search'

/** What a host page can seed the builder with — parseDql output, page-shaped. */
export interface BuilderSeed {
  streams: string[]
  time: DqlTime
  filters: DqlFilter[]
  aggregation: DqlAggregation | null
  limit: number
}

/** Imperative surface for the host page's cell pivots — see addFilterAndRun. */
export interface BuilderHandle {
  /** Append a filter chip and run the resulting query, in one gesture. */
  addFilterAndRun: (filter: DqlFilter) => void
}

/**
 * The visual builder, as one dense toolbar. Extracted from Datafabric ·
 * Search unchanged; hosts differ only in what they do with onRun.
 *
 * Design rules (this panel was redesigned for compactness and speed):
 * - NO DQL preview. The form compiles to DQL through buildDql exactly as
 *   before, but the query is an implementation detail here — anyone who wants
 *   the text switches to query mode, and the mode chips hand over the
 *   compiled string. The door swings back too, but STRICTLY: the page seeds this
 *   form through parseDql, which only reads builder-shaped queries and
 *   returns null for everything else — a wrong reading would silently run
 *   something other than the text said.
 * - ONE wrapping row, reading left to right the way the query executes:
 *   stream -> time -> filters -> aggregation, with limit and the actions at
 *   the end. No section headings; each control carries its own label.
 * - Enter ANYWHERE in the panel runs the search, so the keyboard path is
 *   type-value-Enter with no travel to the Run button.
 * - Every filter is a bordered chip holding field/op/value, editable in
 *   place — no modes, no popovers, nothing to open before changing a value.
 */
export function BuilderPanel({
  vocabulary,
  vocabularyPending,
  onRun,
  onDqlChange,
  costLabel,
  disabled,
  seed,
  streamRank,
  ref,
}: {
  vocabulary: Record<string, string[]> | undefined
  vocabularyPending: boolean
  onRun: (dql: string, window?: SearchWindow) => void
  /** Reports the live compiled query, so the host can cost-preview it. */
  onDqlChange: (dql: string, window?: SearchWindow) => void
  costLabel: string | null
  disabled: boolean
  /**
   * Initial form state, read ONCE on mount — the panel remounts on every
   * mode switch, so "on mount" is "on entering builder mode". Comes from
   * parseDql over the query text when that text is builder-shaped.
   */
  seed?: BuilderSeed
  /**
   * stream name -> bytes on disk, from the ONLINE stream list — the legacy
   * console's dropdown rule, carried over: streams actively ingesting the
   * most data first, everything unranked after them alphabetically.
   */
  streamRank?: ReadonlyMap<string, number>
  ref?: Ref<BuilderHandle>
}) {
  const streams = Object.keys(vocabulary ?? {}).sort((a, b) => {
    const ra = streamRank?.get(a)
    const rb = streamRank?.get(b)
    if (ra !== undefined && rb !== undefined) return rb - ra
    if (ra !== undefined) return -1
    if (rb !== undefined) return 1
    return a.localeCompare(b)
  })
  // Empty means "no explicit choice yet"; the effective selection is derived,
  // so no effect is needed to sync a default in when the vocabulary lands.
  const [chosenStreams, setChosenStreams] = useState<string[]>(seed?.streams ?? [])
  const selectedStreams = chosenStreams.length ? chosenStreams : streams.slice(0, 1)
  const [time, setTime] = useState<DqlTime>(seed?.time ?? { kind: 'relative', duration: '1h' })
  const [filters, setFilters] = useState<DqlFilter[]>(seed?.filters ?? [])
  const [groupBy, setGroupBy] = useState(seed?.aggregation?.groupBy ?? '')
  const [aggFn, setAggFn] = useState<(typeof AGG_FUNCTIONS)[number]>(seed?.aggregation?.fn ?? 'count')
  const [aggOf, setAggOf] = useState(seed?.aggregation?.of ?? '')
  const [limit, setLimit] = useState(seed?.limit ?? 100)
  const addFilterRef = useRef<HTMLButtonElement>(null)
  /** Index of a just-added filter whose field picker should take focus. */
  const pendingFilterFocus = useRef<number | null>(null)

  /**
   * Move focus into a filter row the user just created with "+ filter". An
   * explicit effect, not an autoFocus prop: the trigger is a React Aria
   * Button, which does not forward autoFocus to the DOM. When the row was
   * added by keyboard, the focus is keyboard-modality and the picker
   * auto-opens — Enter on "+ filter" flows straight into choosing a field.
   * A ref, not state: this is a one-shot instruction to the next render, and
   * consuming it must not itself schedule another render.
   */
  useEffect(() => {
    if (pendingFilterFocus.current === null) return
    const rows = addFilterRef.current?.form?.querySelectorAll('.qbc-filter')
    const btn = rows?.[pendingFilterFocus.current]?.querySelector<HTMLElement>('.pick-btn')
    pendingFilterFocus.current = null
    if (!btn) return
    btn.focus()
    // Open through the picker's own `/` contract rather than relying on the
    // focus event: if the browser considers the element already focused (the
    // restore-focus dance around popover close can leave it so), no focus
    // event fires and focus-based auto-open never triggers.
    btn.dispatchEvent(new KeyboardEvent('keydown', { key: '/', bubbles: true, cancelable: true }))
  })

  // Union of every selected stream's vocabulary, first-seen order. A filter
  // can then name a field that only exists on one of the streams — which is
  // valid DQL, the other streams simply never match it.
  const fields = [
    ...new Set(selectedStreams.flatMap((st) => (vocabulary?.[st] ?? []).map(bareField))),
  ]
  const fieldSet = new Set(fields)

  const setFilter = (i: number, patch: Partial<DqlFilter>) =>
    setFilters((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)))

  /**
   * Changing streams PRUNES rather than resets: filters on fields the new
   * selection still knows survive. A reset was tolerable with single-select;
   * with multi-select every toggle would wipe the form.
   */
  const changeStreams = (next: string[]) => {
    setChosenStreams(next)
    // Prune against the EFFECTIVE selection: an empty choice falls back to
    // the first stream (same rule as selectedStreams), so deselecting the
    // last chip mid-popover must not wipe every filter against ∅.
    const effective = next.length ? next : streams.slice(0, 1)
    const nextFields = new Set(effective.flatMap((st) => (vocabulary?.[st] ?? []).map(bareField)))
    setFilters((fs) => fs.filter((f) => !f.field || nextFields.has(f.field)))
    if (groupBy && !nextFields.has(groupBy)) setGroupBy('')
    if (aggOf && !nextFields.has(aggOf)) setAggOf('')
  }

  const compileWith = (fs: DqlFilter[]) =>
    selectedStreams.length
      ? buildDql({
          stream: selectedStreams,
          time,
          filters: fs,
          aggregation: groupBy && fieldSet.has(groupBy) ? { groupBy, fn: aggFn, of: aggOf || undefined } : null,
          limit,
        })
      : ''
  const dql = compileWith(filters)

  // An absolute window travels beside the query, not inside it.
  const win = time.kind === 'absolute' ? { start: time.start, end: time.end } : undefined

  /**
   * The pivot path INTO the form: the host calls this when a result cell is
   * pivoted while the builder is the active mode, so the filter lands as a
   * chip in the form the user is looking at — not as text in the other mode.
   * Compiles from the appended list directly because the state update has
   * not rendered yet.
   */
  useImperativeHandle(ref, () => ({
    addFilterAndRun: (filter: DqlFilter) => {
      const next = [...filters, filter]
      setFilters(next)
      const q = compileWith(next)
      if (q) onRun(q, win)
    },
  }))

  // Report the compiled query as it changes; the host debounces before asking
  // realtimecost, so this can fire freely on every form edit.
  const winStart = win?.start
  const winEnd = win?.end
  useEffect(() => {
    onDqlChange(dql, winStart && winEnd ? { start: winStart, end: winEnd } : undefined)
  }, [dql, winStart, winEnd, onDqlChange])

  const run = () => {
    if (!disabled && dql) onRun(dql, win)
  }

  /**
   * ⌘↵ / Ctrl+↵ runs from ANYWHERE in the builder — including from inside an
   * open picker popover, which is why this listens on window rather than on
   * the form (popovers render in a portal outside the form's DOM subtree).
   */
  const runRef = useRef(run)
  useEffect(() => {
    runRef.current = run
  })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        runRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (vocabularyPending) {
    return <p className="hud-unit" role="status">Loading streams and fields…</p>
  }
  if (!streams.length) {
    return <p className="hud-unit" role="status">No streams available in this scope.</p>
  }

  return (
    // A real <form>: Enter in any value input submits natively — the keyboard
    // path is type-value-Enter with no travel to the Run button and no
    // synthetic key listeners.
    <form
      className="qbc"
      aria-label="Search builder"
      onSubmit={(e) => {
        e.preventDefault()
        run()
      }}
    >
      <PickList
        label="Streams"
        options={streams}
        selected={selectedStreams}
        onChange={changeStreams}
        multi
        accent
        placeholder="streams…"
        disabled={disabled}
      />

      <TimeRangePicker
        value={time}
        onChange={setTime}
        disabled={disabled}
        // Committing a time answers "when?" — focus walks on to "what else?".
        onCommitted={() => addFilterRef.current?.focus()}
      />

      {filters.map((f, i) => (
        <span className="qbc-filter" key={i}>
          <PickList
            label={`Filter ${i + 1} field`}
            options={fields}
            selected={f.field ? [f.field] : []}
            onChange={(next) => setFilter(i, { field: next[0] ?? '' })}
            placeholder="field…"
          />
          <select
            aria-label={`Filter ${i + 1} operator`}
            className="qbc-op"
            value={f.op}
            onChange={(e) => setFilter(i, { op: e.target.value as DqlFilter['op'] })}
          >
            {DQL_OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
          </select>
          <input
            aria-label={`Filter ${i + 1} value`}
            value={f.value}
            placeholder="value"
            size={Math.max(6, f.value.length + 1)}
            onChange={(e) => setFilter(i, { value: e.target.value })}
          />
          <button
            type="button"
            className="qbc-x"
            aria-label={`Remove filter ${i + 1}`}
            onClick={() => setFilters((fs) => fs.filter((_, j) => j !== i))}
          >
            <Icon icon={X} size={11} />
          </button>
        </span>
      ))}

      <button
        ref={addFilterRef}
        type="button"
        className="qbc-add"
        disabled={disabled}
        onClick={() => {
          // The new row's field picker takes focus on mount; arriving by
          // keyboard (Enter on this button) auto-opens it too, so the flow is
          // Enter -> type a field name -> Enter, with no travel between.
          pendingFilterFocus.current = filters.length
          setFilters((fs) => [...fs, { field: '', op: '=', value: '' }])
        }}
      >
        <Icon icon={Plus} size={11} /> filter
      </button>

      <PickList
        label="Group by"
        options={fields}
        selected={groupBy ? [groupBy] : []}
        onChange={(next) => setGroupBy(next[0] ?? '')}
        placeholder="raw events"
        disabled={disabled}
      />
      {groupBy ? (
        <>
          <select aria-label="Aggregate function" value={aggFn} disabled={disabled} onChange={(e) => setAggFn(e.target.value as typeof aggFn)}>
            {AGG_FUNCTIONS.map((fn) => <option key={fn} value={fn}>{fn}</option>)}
          </select>
          {aggFn !== 'count' ? (
            <PickList
              label="Aggregate field"
              options={fields}
              selected={aggOf ? [aggOf] : []}
              onChange={(next) => setAggOf(next[0] ?? '')}
              placeholder="of field…"
            />
          ) : null}
          <button
            type="button"
            className="qbc-x"
            aria-label="Clear aggregation"
            onClick={() => { setGroupBy(''); setAggOf('') }}
          >
            <Icon icon={X} size={11} />
          </button>
        </>
      ) : null}

      <span className="qbc-spacer" />

      {costLabel ? <span className="q-cost" role="status">{costLabel}</span> : null}

      <select aria-label="Row limit" value={limit} disabled={disabled} onChange={(e) => setLimit(Number(e.target.value))}>
        {[100, 500, 1000, 10000].map((n) => <option key={n} value={n}>{n.toLocaleString('en-US')} rows</option>)}
      </select>

      <button
        type="submit"
        className="bui-btn bui-btn-solid bui-btn-sm"
        disabled={disabled || !dql}
      >
        <Icon icon={Play} size={12} /> Run
      </button>
    </form>
  )
}
